// store/slices/orderSlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  getUserOrders,
  getOrderDetails,
  getAvailableTeamMemberClients,
  addClientTeamMembers,
  addMilestoneNote,
  acceptMilestone,
  requestRevision,
  requestOrderRevision,
  addOrderNote,
  acceptRevision,
} from "../thunks/orderThunks";
import { getThreadDetailsByOrder } from "../thunks/threadThunks";

const initialState = {
  // Client orders
  userOrders: {
    data: [],
    loading: false,
    error: null,
    pagination: {},
  },

  // Single order details
  orderDetails: {
    data: null,
    loading: false,
    error: null,
  },

  // Single thread storage - one thread per tab
  thread: {
    data: null,
    loading: false,
    error: null,
  },

  // Admin orders (existing)
  adminOrders: {
    data: [],
    loading: false,
    error: null,
    pagination: {},
  },

  clientTeamMembers: {
    data: [],
    loading: false,
    error: null,
  },

  availableTeamMembers: {
    data: [],
    loading: false,
    error: null,
  },

  orderNote: {
    loading: false,
    error: null,
    success: false,
  },
};

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    clearOrderDetails: (state) => {
      state.orderDetails = initialState.orderDetails;
      state.orderDetailsData = initialState.orderDetailsData;
    },
    clearUserOrders: (state) => {
      state.userOrders = initialState.userOrders;
    },
    clearAvailableTeamMembers: (state) => {
      state.availableTeamMembers = initialState.availableTeamMembers;
    },
    // Thread-related reducers
    setOrderChat: (state, action) => {
      state.thread.data = action.payload;
    },
    updateLastMessage: (state, action) => {
      const { lastMessage } = action.payload;
      if (state.thread.data) {
        // Normalize sender name if missing
        if (lastMessage?.sender && !lastMessage.sender.name) {
          const { firstName, lastName } = lastMessage.sender;
          if (firstName || lastName) {
            lastMessage.sender.name =
              `${firstName || ""} ${lastName || ""}`.trim();
          }
        }
        state.thread.data.lastMessage = lastMessage;
      }
    },
    updateLastMessageDeliveredToAll: (state, action) => {
      const { isDeliveredToAll } = action.payload;
      if (state.thread.data) {
        state.thread.data.isLastMessageDeliveredToAll = isDeliveredToAll;
      }
    },
    updateLastMessageReadByAll: (state, action) => {
      const { isReadByAll } = action.payload;
      if (state.thread.data) {
        state.thread.data.isLastMessageReadByAll = isReadByAll;
      }
    },
    participantJoined: (state, action) => {
      const { threadId, user, isGroup } = action.payload;
      if (state.thread.data && state.thread.data._id === threadId) {
        // Prevent duplicates
        const exists = state.thread.data.participants.some(
          (p) => p._id === user._id,
        );
        if (!exists) {
          state.thread.data.participants.push(user);
        }
        state.thread.data.isGroup = isGroup;
      }
    },
    participantLeft: (state, action) => {
      const { threadId, userId, isGroup } = action.payload;
      if (state.thread.data && state.thread.data._id === threadId) {
        state.thread.data.participants = state.thread.data.participants.filter(
          (p) => p._id !== userId,
        );
        state.thread.data.isGroup = isGroup;
      }
    },
    updateThreadInfo: (state, action) => {
      const { threadId, name, isCustomName, isGroup } = action.payload;
      if (state.thread.data && state.thread.data._id === threadId) {
        state.thread.data.name = name;
        state.thread.data.isCustomName = isCustomName;
        state.thread.data.isGroup = isGroup;
      }
    },
    clearOrderChat: (state) => {
      state.thread.data = null;
    },
    resetOrderNoteState: (state) => {
      state.orderNote = initialState.orderNote;
    },
  },
  extraReducers: (builder) => {
    // Get User Orders
    builder
      .addCase(getUserOrders.pending, (state) => {
        state.userOrders.loading = true;
        state.userOrders.error = null;
      })
      .addCase(getUserOrders.fulfilled, (state, action) => {
        state.userOrders.loading = false;
        state.userOrders.data = action.payload.data?.orders || [];
        state.userOrders.pagination = action.payload.data?.pagination || {};
      })
      .addCase(getUserOrders.rejected, (state, action) => {
        state.userOrders.loading = false;
        state.userOrders.error =
          action.payload?.message || "Failed to fetch orders";
      });

    // Get Order Details
    builder
      .addCase(getOrderDetails.pending, (state) => {
        state.orderDetails.loading = true;
        state.orderDetails.error = null;
      })
      .addCase(getOrderDetails.fulfilled, (state, action) => {
        state.orderDetails.loading = false;
        state.orderDetails.data = action.payload.data;
      })
      .addCase(getOrderDetails.rejected, (state, action) => {
        state.orderDetails.loading = false;
        state.orderDetails.error =
          action.payload?.message || "Failed to fetch order details";
      });

    // Get Available Team Members
    builder
      .addCase(addOrderNote.pending, (state) => {
        state.orderNote.loading = true;
        state.orderNote.error = null;
        state.orderNote.success = false;
      })
      .addCase(addOrderNote.fulfilled, (state, action) => {
        state.orderNote.loading = false;
        state.orderNote.success = true;

        // Update order details with new comment
        if (state.orderDetails.data) {
          // Add new comment to orderComments array (at the beginning for latest first)
          const newComment = action.payload.data.comment;

          // Initialize if doesn't exist
          if (!state.orderDetails.data.comments) {
            state.orderDetails.data.comments = {};
          }
          if (!state.orderDetails.data.comments.orderComments) {
            state.orderDetails.data.comments.orderComments = [];
          }

          // Add new comment at the beginning
          state.orderDetails.data.comments.orderComments.unshift(newComment);

          // Update total comment count
          state.orderDetails.data.comments.totalCommentCount =
            (state.orderDetails.data.comments.totalCommentCount || 0) + 1;

          // Set hasUnreadActivity to true
          state.orderDetails.data.comments.hasUnreadActivity = true;
        }
      })
      .addCase(addOrderNote.rejected, (state, action) => {
        state.orderNote.loading = false;
        state.orderNote.error =
          action.payload?.message || "Failed to add order note";
        state.orderNote.success = false;
      });
    builder
      .addCase(getAvailableTeamMemberClients.pending, (state) => {
        state.availableTeamMembers.loading = true;
        state.availableTeamMembers.error = null;
      })
      .addCase(getAvailableTeamMemberClients.fulfilled, (state, action) => {
        state.availableTeamMembers.loading = false;
        state.availableTeamMembers.data =
          action.payload.data?.availableTeamMembers || [];
      })
      .addCase(getAvailableTeamMemberClients.rejected, (state, action) => {
        state.availableTeamMembers.loading = false;
        state.availableTeamMembers.error =
          action.payload?.message || "Failed to fetch available team members";
      });

    // Get Thread Details By Order
    builder
      .addCase(getThreadDetailsByOrder.pending, (state) => {
        state.thread.loading = true;
        state.thread.error = null;
      })
      .addCase(getThreadDetailsByOrder.fulfilled, (state, action) => {
        state.thread.loading = false;
        const { thread } = action.payload.data;
        state.thread.data = thread;
      })
      .addCase(getThreadDetailsByOrder.rejected, (state, action) => {
        state.thread.loading = false;
        state.thread.error = action.payload || "Failed to fetch thread details";
      });

    // Add Client Team Members
    builder
      .addCase(addClientTeamMembers.pending, (state) => {
        // You can add loading state here if needed
      })
      .addCase(addClientTeamMembers.fulfilled, (state, action) => {
        // Success handled in component - we'll refresh order details
      })
      .addCase(addClientTeamMembers.rejected, (state, action) => {
        // Error handled in component
      });

    // Add Milestone Note
    builder
      .addCase(addMilestoneNote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMilestoneNote.fulfilled, (state, action) => {
        state.loading = false;

        if (state.orderDetails?.data?.progress?.milestoneProgress) {
          // Find and update the milestone with new comment
          const milestoneIndex =
            state.orderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) => milestone._id === action.payload.milestoneId,
            );

          if (milestoneIndex !== -1) {
            // Add new comment to the milestone's comments array
            if (
              !state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].comments
            ) {
              state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].comments = [];
            }

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].comments.push(action.payload.comment);

            // Update comment count
            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ]._commentCount = action.payload.milestoneCommentCount;
          }

          // Update total comment count
          state.orderDetails.data.comments.totalCommentCount =
            action.payload.totalOrderComments;
        }
      })
      .addCase(addMilestoneNote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Accept Milestone
    builder
      .addCase(acceptMilestone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptMilestone.fulfilled, (state, action) => {
        state.loading = false;

        if (state.orderDetails?.data?.progress?.milestoneProgress) {
          const milestoneIndex =
            state.orderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) =>
                milestone._id === action.payload.data.milestone._id,
            );

          if (milestoneIndex !== -1) {
            // Update milestone progress status and timeline
            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].progressStatus = action.payload.data.milestone.progressStatus;

            // Update revision stage if present
            if (action.payload.data.milestone.revisionStage !== undefined) {
              state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].revisionStage = action.payload.data.milestone.revisionStage;
            }

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].timeline = {
              ...state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].timeline,
              ...action.payload.data.milestone.timeline,
            };

            // Update accepted and completed dates
            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].acceptedAt = action.payload.data.milestone.acceptedAt;

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].completedAt = action.payload.data.milestone.completedAt;
          }

          // Update order status if all milestones completed
          if (action.payload.data.order.status) {
            state.orderDetails.data.status = action.payload.data.order.status;
          }

          // Update order timeline
          if (action.payload.data.order.timeline) {
            state.orderDetails.data.timeline = {
              ...state.orderDetails.data.timeline,
              ...action.payload.data.order.timeline,
            };
          }

          // Update completion percentage
          const completedMilestones =
            state.orderDetails.data.progress.milestoneProgress.filter(
              (m) =>
                m.progressStatus === "COMPLETED" ||
                m.progressStatus === "ACCEPTED",
            ).length;

          const totalMilestones =
            state.orderDetails.data.progress.milestoneProgress.length;
          state.orderDetails.data.progress.completionPercentage =
            totalMilestones > 0
              ? Math.round((completedMilestones / totalMilestones) * 100)
              : 0;
        }
      })
      .addCase(acceptMilestone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Request Revision
    builder
      .addCase(requestRevision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestRevision.fulfilled, (state, action) => {
        state.loading = false;

        if (state.orderDetails?.data?.progress?.milestoneProgress) {
          const milestoneIndex =
            state.orderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) =>
                milestone._id === action.payload.data.milestone._id,
            );

          if (milestoneIndex !== -1) {
            // Update milestone progress status and timeline
            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].progressStatus = action.payload.data.milestone.progressStatus;

            // Update revision stage
            if (action.payload.data.milestone.revisionStage !== undefined) {
              state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].revisionStage = action.payload.data.milestone.revisionStage;
            }

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].timeline = {
              ...state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].timeline,
              ...action.payload.data.milestone.timeline,
            };

            // Update revision details
            if (action.payload.data.milestone.revisionDetails) {
              state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].revisionDetails = action.payload.data.milestone.revisionDetails;
            }

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].revisionRequested =
              action.payload.data.milestone.revisionRequested;

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].revisionNotes = action.payload.data.milestone.revisionNotes;

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].revisionCount = action.payload.data.milestone.revisionCount;

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].revisionRequestedAt =
              action.payload.data.milestone.revisionRequestedAt;

            // Clear accepted and completed dates
            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].acceptedAt = null;
            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].completedAt = null;

            // Add revision attachments if any
            if (action.payload.data.milestone.attachments) {
              const existingAttachments =
                state.orderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ].attachments || [];

              // Filter out duplicates based on _id if needed, or just append
              // Assuming attachments in payload are NEW attachments
              // But strictly speaking we might want to merge carefully.
              // For now, let's just use the logic that was there but maybe safer?
              // The previous code was:
              // state.orderDetails.data.progress.milestoneProgress[milestoneIndex].attachments = [...existingAttachments, ...action.payload.data.milestone.attachments];

              // Let's verify if we are duplicating. Usually payload returns the *updated* object or just the new stuff.
              // If backend returns the full list of attachments for the milestone, we should replace.
              // If it returns only new ones, append.
              // Based on Thunk implementation `requestRevision` returns `response.data`.
              // Usually backend returns the updated milestone object.
              // If so, `action.payload.data.milestone.attachments` might be the full list?
              // The usage `...existingAttachments, ...action.payload.data.milestone.attachments` suggests the payload contains ONLY NEW attachments or the previous dev assumed so.
              // Without backend code, unsafe to assume.
              // However, typically `attachments` on the milestone object in DB contains ALL attachments.
              // If `action.payload.data.milestone` is the "updated milestone", then `attachments` SHOULD be the full list.
              // If so, `[...existing, ...new]` would duplicate.
              // Let's rely on the property that we're likely receiving the UPDATED milestone document structure.
              // So replacing might be safer IF it's the full list.
              // OR, ensuring uniqueness.

              const newAttachments = action.payload.data.milestone.attachments;
              const uniqueAttachments = [...existingAttachments];

              newAttachments.forEach((newAtt) => {
                if (
                  !uniqueAttachments.some(
                    (existing) => existing._id === newAtt._id,
                  )
                ) {
                  uniqueAttachments.push(newAtt);
                }
              });

              state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].attachments = uniqueAttachments;
            }
          }

          // Update order status
          if (action.payload.data.order.status) {
            state.orderDetails.data.status = action.payload.data.order.status;
          }

          // Update order timeline
          if (action.payload.data.order.timeline) {
            state.orderDetails.data.timeline = {
              ...state.orderDetails.data.timeline,
              ...action.payload.data.order.timeline,
            };
          }
        }
      })
      .addCase(requestRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Request Order-level Revision
    builder
      .addCase(requestOrderRevision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestOrderRevision.fulfilled, (state, action) => {
        state.loading = false;

        if (state.orderDetails?.data) {
          // Update order status
          if (action.payload.data.order.status) {
            state.orderDetails.data.status = action.payload.data.order.status;
          }

          // Update order timeline
          if (action.payload.data.order.timeline) {
            state.orderDetails.data.timeline = {
              ...state.orderDetails.data.timeline,
              ...action.payload.data.order.timeline,
            };
          }
        }
      })
      .addCase(requestOrderRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Accept Revision
    builder
      .addCase(acceptRevision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptRevision.fulfilled, (state, action) => {
        state.loading = false;

        if (state.orderDetails?.data?.progress?.milestoneProgress) {
          const milestoneIndex =
            state.orderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) =>
                milestone._id === action.payload.data.milestone._id,
            );

          if (milestoneIndex !== -1) {
            // Update milestone progress status and timeline
            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].progressStatus = action.payload.data.milestone.progressStatus;

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].revisionStage = action.payload.data.milestone.revisionStage; // Should be REVISION_COMPLETED

            state.orderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].timeline = {
              ...state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].timeline,
              ...action.payload.data.milestone.timeline,
            };

            // Update revision details if provided
            if (action.payload.data.milestone.revisionDetails) {
              state.orderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].revisionDetails = action.payload.data.milestone.revisionDetails;
            }
          }

          // Update order status if changed
          if (action.payload.data.order && action.payload.data.order.status) {
            state.orderDetails.data.status = action.payload.data.order.status;
          }

          // Update order timeline if changed
          if (action.payload.data.order && action.payload.data.order.timeline) {
            state.orderDetails.data.timeline = {
              ...state.orderDetails.data.timeline,
              ...action.payload.data.order.timeline,
            };
          }
        }
      })
      .addCase(acceptRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearOrderDetails,
  clearUserOrders,
  clearAvailableTeamMembers,
  setOrderChat,
  updateLastMessage,
  updateLastMessageDeliveredToAll,
  updateLastMessageReadByAll,
  clearOrderChat,
  resetMilestoneLoading,
  resetOrderNoteState,
  updateMilestoneProgress,
  clearMilestoneError,
  participantJoined,
  participantLeft,
  updateThreadInfo,
} = orderSlice.actions;

export default orderSlice.reducer;
